import React, { useCallback, useEffect, useState } from "react";
import Select from "react-select";
import * as Styled from "./SupportTicketingComponent.styled";
import SearchIcon from "../../assets/images/icons/search.svg";
import TicketIcon from "../../assets/images/icons/pencil.svg";
import AllTicketsIcon from "../../assets/images/icons/all_tickets.svg";
import OngoingIcon from "../../assets/images/icons/ongoing.svg";
import PendingIcon from "../../assets/images/icons/pending.svg";
import ResolvedIcon from "../../assets/images/icons/resolved.svg";
import ClosedIcon from "../../assets/images/icons/closed.svg";
import { TicketList } from "./TicketList";
import { TicketModal } from "./TicketModal";
import { AddReplyPayload, CreateTicketRequestDto, DateFilter, FileWithPreview, getDateRange, MultiPartUploader, UpdateTicketStatusAdminPayload } from "../../libs";
import { useFormik } from "formik";
import * as Yup from "yup";
import { SupportService } from "../../services/support-service";
import { useAuth, useGlobalState } from "../../behavioral";
import { toast } from "react-toastify";
import { Loader } from "../ui";
import { userService } from "../../services";

const getStatusIcon = (statusName: string) => {
    const iconMap: Record<string, string> = {
        "New": AllTicketsIcon,
        "In Progress": OngoingIcon,
        "Awaiting Response": PendingIcon,
        "Cancelled": ResolvedIcon,
        "Closed": ClosedIcon,
    };
    return iconMap[statusName] || AllTicketsIcon;
};

export const SupportTicketingComponent: React.FC = () => {
    const [activeTab, setActiveTab] = useState<number | null>(null);
    const [selectedDateFilter, setSelectedDateFilter] = useState<string>("");
    const [showModal, setShowModal] = useState(false);
    const [modalContent, setModalContent] = useState<"add" | "update">("add");
    const [selectedTicket, setSelectedTicket] = useState<any | null>(null);
    const [uploadedImages, setUploadedImages] = useState<FileWithPreview[]>([]);
    const [selectedPriority, setSelectedPriority] = useState<number | null>(null);
    const [priorityOptions, setPriorityOptions] = useState<{ value: number; label: JSX.Element }[]>([]);
    const [statusOptions, setStatusOptions] = useState<{ value: number | null; label: JSX.Element; forAdmin: boolean; isDisabled?: boolean; }[]>([]);
    const [ticketTypes, setTicketTypes] = useState<{ value: number; label: JSX.Element }[]>([]);
    const [selectedTicketDetails, setSelectedTicketDetails] = useState<any | null>(null);
    const [tickets, setTickets] = useState<any[]>([]);
    const [ticketReplies, setTicketReplies] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [percentage, setPercentage] = React.useState(0);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [userEmail, setUserEmail] = useState<string>("");

    const { state } = useGlobalState();
    const authInfo = useAuth();
    const isAdmin = state.isAdmin;
    const [selectedDropdown, setselectedDropdown] = useState({
        ticketType: '',
        currentStatus: '',
        priority: ''
    })
    const dateFilterOptions = Object.values(DateFilter).map((filter) => ({
        label: filter,
        value: filter,
    }));

    const handleNewTicket = () => {
        setModalContent("add");
        setSelectedTicket(null);
        setShowModal(true);
    };

    const handleOpenTicket = useCallback((ticket: any) => {
        setSelectedTicket(ticket);
        setModalContent("update");
        setShowModal(true);
        // Fetch replies only when modal is in update mode
        if (ticket?.ticketId) {
            fetchReplies(ticket.ticketId);
            fetchTicketDetails(ticket.ticketId);
        }
    }, []);

    const handleCloseModal = useCallback(() => {
        setShowModal(false);
        setUploadedImages([])
        formik.resetForm();
        setSelectedTicketDetails(null);
        setSelectedTicket(null);
    }, []);


    // Fetch priority options from API
    const fetchPriorities = useCallback(async () => {
        try {
            const response = await SupportService.getAllTicketPriorities();
            const formattedOptions = response.priorities.map((priority: { id: number; name: string; color: string }) => ({
                value: priority.id,
                label: (
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <Styled.PriorityDot style={{ backgroundColor: priority.color }} />
                        {priority.name}
                    </div>
                ),
            }));
            setPriorityOptions(formattedOptions);
        } catch (error) {
            console.error("Error fetching priorities:", error);
        }
    }, []);

    const fetchStatuses = useCallback(async () => {
        try {
            const response = await SupportService.getAllTicketStatuses();

            const allTicketsTab = {
                value: null,
                label: (
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <Styled.PriorityDot style={{ backgroundColor: "#17a2b8" }} />
                        All Tickets
                    </div>
                ),
                forAdmin: false,
                isDisabled: false
            };

            const formattedStatuses = response.statuses.map((status: { id: number; name: string; color: string; forAdmin: boolean }) => ({
                value: status.id,
                label: (
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <Styled.PriorityDot style={{ backgroundColor: status.color }} />
                        {status.name}
                    </div>
                ),
                forAdmin: status.forAdmin,
                isDisabled: !isAdmin && status.forAdmin
            }));

            setStatusOptions([allTicketsTab, ...formattedStatuses]);
        } catch (error) {
            console.error("Error fetching statuses:", error);
        }
    }, [isAdmin]);



    const fetchTicketTypes = useCallback(async () => {
        try {
            const responseData = await SupportService.getAllTicketTypes();

            if (!responseData || !responseData.requestTypes) {
                throw new Error("Invalid API response structure");
            }

            const formattedOptions = responseData.requestTypes.map((type: { id: number; name: string }) => ({
                value: type.id,
                label: type.name,
            }));
            setTicketTypes(formattedOptions);
        } catch (error) {
            console.error("Error fetching ticket types:", error);
        }
    }, []);


    const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | null>(null);
    const [paginationHistory, setPaginationHistory] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    //const ticketsPerPage = 50;

    const fetchTickets = useCallback(async (
        dateFilter: string = selectedDateFilter,
        statusId: number | null = activeTab,
        key: string | null | undefined = undefined,
        priority: number | null = selectedPriority
    ) => {
        setLoading(true);
        try {

            const { startDate, endDate } = getDateRange(dateFilter);
            const queryParams: Record<string, any> = {};

            if (startDate != '' && endDate != '') {
                queryParams.startDate = startDate;
                queryParams.endDate = endDate;
            }

            if (statusId !== null) {
                queryParams.status = statusId;
            }

            if (priority !== null) {
                queryParams.priority = priority;
            }

            if (searchQuery) {
                queryParams.search = searchQuery;
            }

            if (key) {
                queryParams.lastEvaluatedKey = key;
            }

            // queryParams.limit = ticketsPerPage; 
            const data = await SupportService.getAllTickets(queryParams);
            setTickets(data.tickets || []);
            setLastEvaluatedKey(data.pagination?.lastEvaluatedKey ?? null);

            if (key) {
                // Only add the key to history if it doesn't already exist (avoid duplicates)
                setPaginationHistory((prev) => {
                    if (!prev.includes(key)) {
                        return [...prev, key];
                    }
                    return prev;
                });
            }

        } catch (error) {
            console.error("Error fetching tickets:", error);
        } finally {
            setLoading(false);
        }
    }, [selectedDateFilter, activeTab, selectedPriority, searchQuery, lastEvaluatedKey, paginationHistory]);

    const nextPage = () => {
        if (lastEvaluatedKey) {
            fetchTickets(selectedDateFilter, activeTab, lastEvaluatedKey);
            setCurrentPage((prev) => prev + 1);
        }
    };

    // Previous Page Function with History Reset
const prevPage = () => {
    if (currentPage === 2) {
        // If navigating back to page 1, clear the history
        setPaginationHistory([]);
        fetchTickets(selectedDateFilter, activeTab, undefined);  // Fetch page 1 without key
        setCurrentPage(1);
    } else if (paginationHistory.length > 1) {
        // Otherwise, navigate back as usual
        const prevKey = paginationHistory[paginationHistory.length - 2] || undefined;

        // Remove the last key from history
        setPaginationHistory((prev) => prev.slice(0, -1));

        fetchTickets(selectedDateFilter, activeTab, prevKey);
        setCurrentPage((prev) => prev - 1);
    }
};

    const handleTabClick = (statusId: number | null) => {
        setActiveTab(statusId);
        setPaginationHistory([]);  
        setCurrentPage(1)
        if (statusId === null) {
            fetchTickets(selectedDateFilter, null);
        } else {
            fetchTickets(selectedDateFilter, statusId);
        }
    };

    const fetchReplies = async (ticketId: string) => {
        try {
            const response = await SupportService.getRepliesByTicketId(ticketId);
            if (response?.replies) {
                setTicketReplies(response.replies);
            } else {
                toast.error("No replies found for this ticket.");
            }
        } catch (error: any) {
            toast.error(error?.message || "Failed to fetch replies. Please try again.");
        }
    };
    const getLoggedInUser = React.useCallback(async () => {
        try {
            const currentUser = await userService.getCurrentUser();
            if (currentUser?.email) {
                setUserEmail(currentUser.email);
            }

        } catch (error) {
            console.error("Failed to fetch current user:", error);
        }
    }, []);

    React.useEffect(() => {
        getLoggedInUser();
    }, [getLoggedInUser]);

    useEffect(() => {
        fetchPriorities();
    }, []);

    // Fetch statuses
    useEffect(() => {
        fetchStatuses();
    }, []);

    // Fetch ticket types
    useEffect(() => {
        fetchTicketTypes();
    }, []);

    // Fetch tickets
    useEffect(() => {
        fetchTickets();
    }, []);

    const validationSchema = Yup.object({
        email: isAdmin
            ? (modalContent === "update"
                ? Yup.string() // No validation for email when isAdmin is true and update
                : Yup.string().email("Invalid email format").required("Email is required"))
            : (modalContent === "add"
                ? Yup.string() // No validation for email when isAdmin is false and add
                : Yup.string().email("Invalid email format")),
        ticketType: Yup.string().required("Request Ticket Type is required"),
        priority: Yup.string().required("Priority is required"),
        title: isAdmin && modalContent === "update"
            ? Yup.string()
            : Yup.string()
                .trim()
                .required("Ticket Title is required")
                .max(100, "Ticket Title cannot exceed 100 characters"),
    });

    const formik = useFormik({
        initialValues: {
            email: "",
            ticketType: "",
            priority: "",
            title: "",
            message: "",
            currentStatus: "",
        },
        validationSchema: modalContent === "update" && !isAdmin ? null : validationSchema,
        validateOnChange: !(modalContent === "update" && !isAdmin),
        validateOnBlur: !(modalContent === "update" && !isAdmin),

        onSubmit: async (
            values: {
                email: string;
                ticketType: string;
                priority: string;
                title: string;
                message: string;
                currentStatus: string;
            },
            { resetForm }: { resetForm: () => void }
        ) => {
            try {
                let s3Responses: { fileName: string; s3Key: string }[] = [];
                let existingAttachments: { fileName: string; s3Key: string }[] = [];

                // Get dynamic clientId
                const clientId = authInfo?.user?.clientId;

                // Separate already uploaded files and new images
                uploadedImages.forEach((img: any) => {
                    if (img.s3Key) {
                        existingAttachments.push({
                            fileName: img.fileName,
                            s3Key: img.s3Key,
                        });
                    }
                });

                const newImages = uploadedImages.filter((img: any) => img.file && !img.s3Key);

                // Upload new images
                if (newImages.length > 0) {
                    const uploadPromises = newImages.map((image: any) => {
                        return new Promise<void>((resolve, reject) => {
                            const uploader = new MultiPartUploader({
                                fileName: image.file.name,
                                file: image.file,
                                clientId: authInfo?.user?.username,
                                mediaId: "support-ticket-attachment",
                                category: "image",
                            });

                            uploader
                                .getOnProgress((params: any) => {
                                    setPercentage(params.percentage);
                                })
                                .getOnComplete((params: any) => {
                                    s3Responses.push({
                                        fileName: image.file.name,
                                        s3Key: params.s3Key,
                                    });
                                    resolve();
                                })
                                .getOnError((error: any) => {
                                    console.error("Upload error:", error);
                                    toast.error("Error uploading image file");
                                    reject(error);
                                });
                            uploader.start();
                        });
                    });

                    await Promise.all(uploadPromises);
                }

                // Prepare common attachments
                const attachments = [...existingAttachments, ...s3Responses];

                // Check if adding or updating
                if (modalContent === "add") {
                    // Create the payload
                    const createTicketPayload: CreateTicketRequestDto = isAdmin
                        ? {
                            clientId,
                            user: { email: values.email },
                            statusId: Number(values.currentStatus),
                            priorityId: Number(values.priority),
                            requestType: Number(values.ticketType),
                            title: values.title,
                            message: values.message,
                            attachments: attachments,
                        }
                        : {
                            clientId,
                            user: { email: userEmail },
                            statusId: 1,
                            priorityId: Number(values.priority),
                            requestType: Number(values.ticketType),
                            title: values.title,
                            message: values.message,
                            attachments: attachments,
                        };

                    // Send the payload
                    const response = await SupportService.createTicket(createTicketPayload as any);

                    // Show backend message or fallback
                    if (response?.data?.ticketId) {
                        toast.success(`Ticket created successfully! ID: ${response.data.ticketId}`);
                    } else {
                        toast.error(response?.error?.message || "Failed to create ticket.");
                    }

                } else if (modalContent === "update") {
                    const isReplyEmpty = !values.title.trim() && !values.message.trim();
                    const hasAttachments = attachments.length > 0;
                    const ticketId = selectedTicketDetails?.ticketId || "";

                    if (!ticketId) {
                        toast.error("Ticket ID is missing!");
                        return;
                    }

                    try {
                        if (isAdmin) {
                            // ✅ Admin Logic: Update status, priority, and type if changed
                            const statusChanged = values.currentStatus !== selectedDropdown.currentStatus;
                            const priorityChanged = values.priority !== selectedDropdown.priority;
                            const typeChanged = values.ticketType !== selectedDropdown.ticketType;

                            if (
                                values.currentStatus !== "Closed" &&
                                values.currentStatus !== "New" &&
                                (statusChanged || priorityChanged || typeChanged)
                            ) {
                                const updatePayload: UpdateTicketStatusAdminPayload = {
                                    statusId: Number(values.currentStatus),
                                    priorityId: Number(values.priority),
                                    requestType: Number(values.ticketType),
                                };

                                const updateResponse = await SupportService.updateSingleByTicketId(ticketId, updatePayload);

                                if (updateResponse?.message) {
                                    toast.success(updateResponse.message || "Ticket updated successfully.");
                                } else {
                                    toast.error(updateResponse?.message || "Failed to update ticket.");
                                }
                            }
                        }

                        // ✅ Add Reply (if applicable)
                        const statusUnchanged =
                            values.currentStatus === selectedDropdown.currentStatus &&
                            values.priority === selectedDropdown.priority &&
                            values.ticketType === selectedDropdown.ticketType;

                        if (
                            values.currentStatus !== "Closed" &&
                            values.currentStatus !== "New" &&
                            statusUnchanged
                        ) {
                            if (isReplyEmpty && !hasAttachments) {
                                toast.error("Please add a message or attach a file.");
                                return;
                            }

                            const replyPayload: AddReplyPayload = {
                                title: values.title,
                                message: values.message,
                                attachments,
                            };

                            const replyResponse = await SupportService.addReplyToTicket(ticketId, replyPayload);

                            if (replyResponse?.error) {
                                throw new Error(replyResponse.error || "Failed to add reply.");
                            }
                        }

                        // ✅ Handle Closed or New status
                        if (values.currentStatus === "Closed" || values.currentStatus === "New") {
                            const closedStatus = statusOptions.find(opt => opt.label.props.children[1] === "Closed")?.value;
                            const newStatus = statusOptions.find(opt => opt.label.props.children[1] === "New")?.value;

                            const updatePayload: UpdateTicketStatusAdminPayload = {
                                statusId: Number(
                                    values.currentStatus === "Closed"
                                        ? closedStatus
                                        : values.currentStatus === "New"
                                            ? newStatus
                                            : values.currentStatus
                                ),
                                priorityId: Number(values.priority),
                                requestType: Number(values.ticketType),
                            };

                            const updateResponse = await SupportService.updateSingleByTicketId(ticketId, updatePayload);

                            if (updateResponse?.message) {
                                toast.success(updateResponse.message || "Ticket updated successfully.");
                            } else {
                                toast.error(updateResponse?.message || "Failed to update ticket.");
                            }
                        }
                    } catch (error: any) {
                        console.error("Error during ticket update:", error);
                        toast.error(error?.message || "Failed to update the ticket.");
                    }
                }

                resetForm();
                setUploadedImages([]);
                handleCloseModal();
                fetchTickets();

            } catch (error: any) {
                console.error("Error submitting ticket:", error);
                toast.error(error?.message || "Failed to submit the ticket.");
            }
        },


    });

    const fetchTicketDetails = useCallback(async (ticketId: string) => {
        try {
            setLoading(true);
            const ticket = await SupportService.getSingleByTicketId(ticketId);

            if (ticket) {
                console.log("data:::", ticket?.requestType?.toString(), ticket?.status?.id?.toString(), ticket?.priority?.id?.toString());
                setselectedDropdown({
                    ticketType: ticket?.requestType?.toString(),
                    currentStatus: ticket?.status?.id?.toString(),
                    priority: ticket?.priority?.id?.toString(),
                });

                formik.setFieldValue("ticketType", ticket?.requestType?.toString() || "");
                formik.setFieldValue("currentStatus", ticket?.status?.id?.toString() || "");
                formik.setFieldValue("priority", ticket?.priority?.id?.toString() || "");
                setSelectedTicketDetails(ticket);
            } else {
                toast.error("No ticket details found.");
            }
        } catch (error: any) {
            console.error("Error fetching ticket details:", error);
            toast.error(error?.message || "Failed to fetch ticket details.");
        } finally {
            setLoading(false);
        }
    }, [formik]);

    useEffect(() => {
        if (searchQuery === "") {
            fetchTickets(selectedDateFilter, activeTab, undefined, selectedPriority);
            setPaginationHistory([]);  
            setCurrentPage(1);
        }
    }, [searchQuery]);
    return (
        <Styled.SupportTicketContainer fluid className="pt-10">
            <Styled.SupportTicketRow>
                <Styled.TicketDetailsCol md={12}>
                    <Styled.BigTitle>My Support Tickets</Styled.BigTitle>
                    <Styled.HeaderWrapper>
                        <Styled.DashboardSearchWrapper>
                            <Styled.SearchBarWrapper>
                                <Styled.SearchInput
                                    name="search"
                                    type="text"
                                    placeholder="Search for ticket"
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                    }}
                                    onKeyPress={(e) => {
                                        if (e.key === "Enter") {
                                            fetchTickets(selectedDateFilter, activeTab, undefined, selectedPriority);
                                            setPaginationHistory([]);  
                                            setCurrentPage(1);
                                        }
                                    }}
                                />
                                <Styled.SearchIcon
                                    src={SearchIcon}
                                    onClick={() => {
                                        fetchTickets(selectedDateFilter, activeTab, undefined, selectedPriority);
                                    }}
                                    style={{ cursor: "pointer" }}
                                />
                            </Styled.SearchBarWrapper>

                        </Styled.DashboardSearchWrapper>

                        <Styled.ControlsContainer>
                            <Select
                                className="custom-select"
                                menuPlacement="auto"
                                styles={Styled.selectStyles}
                                menuShouldScrollIntoView={false}
                                placeholder="Select Priority"
                                options={priorityOptions}
                                isClearable
                                value={priorityOptions.find(option => option.value === selectedPriority)}
                                onChange={(selectedOption) => {
                                    const priorityValue = selectedOption?.value || null;
                                    setSelectedPriority(priorityValue);
                                    setPaginationHistory([]);  
                                    setCurrentPage(1)
                                    fetchTickets(selectedDateFilter, activeTab, undefined, priorityValue);
                                }}
                            />
                            {isAdmin && (
                                <Select
                                    className="custom-select"
                                    menuPlacement="auto"
                                    styles={Styled.selectStyles}
                                    menuShouldScrollIntoView={false}
                                    placeholder="Select Date Range"
                                    options={dateFilterOptions}
                                    isClearable
                                    value={dateFilterOptions.find(option => option.value === selectedDateFilter)}
                                    onChange={(newValue) => {
                                        const filterValue = newValue?.value || "";
                                        setSelectedDateFilter(filterValue);
                                        setPaginationHistory([]);  
                                        setCurrentPage(1)
                                        fetchTickets(filterValue, activeTab, undefined, selectedPriority);
                                    }}
                                />
                            )}
                            <Styled.NewTicketButton onClick={handleNewTicket}>
                                <Styled.Icon src={TicketIcon} alt="New Ticket" />
                                New Ticket
                            </Styled.NewTicketButton>
                        </Styled.ControlsContainer>
                    </Styled.HeaderWrapper>
                    <Styled.TabsContainer>
                        {statusOptions.map((status, index) => {
                            const icon = getStatusIcon(
                                status.value === null ? "All Tickets" : status.label.props.children[1]
                            );

                            return (
                                <Styled.TabItem
                                    key={index}
                                    className={status.value === activeTab ? "active" : ""}
                                    onClick={() => handleTabClick(status.value)}
                                    style={{
                                        backgroundColor: "transparent",
                                        color: status.value === activeTab
                                            ? status.label.props.children[0].props.style.backgroundColor
                                            : "#fff"
                                    }}
                                >
                                    <img
                                        src={icon}
                                        alt={status.value === null ? "All Tickets" : status.label.props.children[1]}
                                    />
                                    <span>{status.value === null ? "All Tickets" : status.label.props.children[1]}</span>
                                </Styled.TabItem>
                            );
                        })}
                    </Styled.TabsContainer>
                    <Styled.TabBorderLine />
                    {loading ? (
                        <Loader />
                    ) : (
                        <TicketList
                            tickets={tickets}
                            currentPage={currentPage}
                            lastEvaluatedKey={lastEvaluatedKey}
                            onNextPage={nextPage}
                            onPrevPage={prevPage}
                            onOpenTicket={handleOpenTicket}
                            isAdmin={isAdmin}
                            paginationHistory={paginationHistory}
                            onNewTicketClick={handleNewTicket}
                        />
                    )}
                </Styled.TicketDetailsCol>
            </Styled.SupportTicketRow>
            {loading ? (
                <Loader />
            ) : (
                <TicketModal show={showModal} onHide={handleCloseModal} setUploadedImages={setUploadedImages} uploadedImages={uploadedImages} content={modalContent} ticket={selectedTicket} ticketReplies={ticketReplies} isAdmin={isAdmin} formik={formik} resetForm={formik.resetForm} fetchPriorities={priorityOptions} fetchStatuses={statusOptions} fetchTicketTypes={ticketTypes} percentage={percentage} />
            )}
        </Styled.SupportTicketContainer>
    );
};
